/**
 * アイコンプレビューステップ
 */
import { useState, useCallback } from "react";
import { UserAvatar } from "common/elements";
import I18n from "features/shared/i18n";
import { GenerationStatus } from "features/player/types";
import { CircularProgress } from "features/shared/stubs";

interface IconPreviewStepProps {
  status: GenerationStatus;
  progress: number;
  generatedImage: string | null;
  partialImage: string | null;
  additionalPrompt?: string;
  onAdditionalPromptChange?: (prompt: string) => void;
  onRegenerate?: () => void;
}

export const IconPreviewStep = ({
  status,
  progress,
  generatedImage,
  partialImage,
  additionalPrompt = "",
  onAdditionalPromptChange,
  onRegenerate,
}: IconPreviewStepProps) => {
  const [localPrompt, setLocalPrompt] = useState(additionalPrompt);
  const isGenerating = status === GenerationStatus.GENERATING;
  const isComplete = status === GenerationStatus.COMPLETE;
  const displayImage = generatedImage || partialImage;

  const handlePromptChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.slice(0, 20);
      setLocalPrompt(value);
      onAdditionalPromptChange?.(value);
    },
    [onAdditionalPromptChange],
  );

  return (
    <div className="flex-col-el flex-center w-full h-full">
      {/* プレビュー領域 */}
      {displayImage && !isGenerating ? (
        <UserAvatar size={200} icon={displayImage} />
      ) : (
        <div className="flex-col-el flex-center w-[200px] h-[200px] bg-gray2-20 rounded-full border-4 border-gray2-40 overflow-hidden">
          {isGenerating ? (
            <CircularProgress progress={progress} size={184} />
          ) : (
            <span className="material-symbols-outlined text-gray2-60 !text-[60px]">
              auto_awesome
            </span>
          )}
        </div>
      )}

      {/* メッセージ */}
      <p className="text-body text-textcolor-black text-[24px] mt-6">
        {isGenerating
          ? I18n.t("MSG_ICON_GENERATING_BY_PARENT")
          : I18n.t("MSG_ICON_CONFIRM_PROMPT_BY_PARENT")}
      </p>

      {/* 追加プロンプト入力フォーム（生成完了後のみ表示） */}
      {isComplete && onRegenerate && (
        <div className="flex-row-el items-center justify-center gap-4 mt-6">
          <div className="relative">
            <input
              type="text"
              value={localPrompt}
              onChange={handlePromptChange}
              placeholder={I18n.t("MSG_ICON_ADDITIONAL_PROMPT_PLACEHOLDER")}
              maxLength={20}
              className="w-[400px] box-border px-4 py-3 pr-[70px] rounded-[8px] border-4 border-input-focus text-[24px] text-textcolor-black bg-white focus:outline-none transition-all"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[18px] text-gray2-80">
              {localPrompt.length}/20
            </span>
          </div>
          <button
            onClick={onRegenerate}
            className="flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all"
          >
            {I18n.t("MSG_ICON_CREATE_BTN")}
          </button>
        </div>
      )}
    </div>
  );
};
